import React, { useRef, useEffect, useState, useMemo, type RefObject, useId } from 'react';

import classNames from 'classnames';

import AnchorLink from '@helsenorge/designsystem-react/components/AnchorLink';
import Title from '@helsenorge/designsystem-react/components/Title';

import PortableText from '@helsenorge/core-cms/richtext';
import type { PortableTextBlocks } from '@helsenorge/core-cms/richtext/portable-text-utils';
import DictionaryBubble from '@helsenorge/core-framework/prosesshjelp/components/dictionarybubble';
import { getUrlHostname } from '@helsenorge/core-utils/url';
import { isHelsenorgeUrl } from '@helsenorge/framework-utils/url';

import AlphabetPanel from '../AlphabeticalList/AlphabetPanel';
import { useScrollingElementList } from '../AlphabeticalList/helpers';

interface Link {
  title: string;
  wordExplanation: PortableTextBlocks;
  source?: SourceDto;
}

interface SourceDto {
  name: string;
  url: string;
}

export interface LetterGroup {
  letter: string;
  ordforklaringList: Link[];
}

interface AlphabeticalListProps {
  alphabetPanelButtonText: string;
  alphabet: string;
  alphabetPanelTitle: string;
  linksForLetter: string;
  hideAlphabetPanel: boolean;
  letterGroupList: LetterGroup[];
  emptyLetterAriaLabel: string;
}

interface OrdforklaringProps {
  link: Link;
}

const Ordforklaring: React.FC<OrdforklaringProps> = ({ link }) => {
  const [isOpen, setIsOpen] = useState(false);
  const id = useId();

  const linkTarget = link?.source?.url && isHelsenorgeUrl(link.source.url) ? '_self' : '_blank';
  const linkHostname = link?.source?.url && getUrlHostname(link.source.url);
  const linkText = linkTarget === '_blank' ? `${link?.source?.name} (${linkHostname})` : link?.source?.name;

  return (
    <DictionaryBubble
      trigger={<span id={id}>{link.title}</span>}
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      onClose={() => setIsOpen(false)}
      helpBubbleProps={{ ariaLabelledById: id }}
    >
      <PortableText value={link.wordExplanation} />
      {link?.source?.name && (
        <span>
          <AnchorLink target={linkTarget} href={link.source.url}>
            {linkText}
          </AnchorLink>
        </span>
      )}
    </DictionaryBubble>
  );
};

const OppslagsverkPage: React.FunctionComponent<AlphabeticalListProps> = ({
  alphabetPanelButtonText,
  alphabet,
  alphabetPanelTitle,
  linksForLetter,
  hideAlphabetPanel,
  letterGroupList,
  emptyLetterAriaLabel,
}) => {
  const letterList = letterGroupList.map(letterGroup => letterGroup.letter.toUpperCase());
  const alphabetLetterList = useMemo(() => [...alphabet], [alphabet]);
  const alphabetPanelRef = useRef<HTMLDivElement>(null);

  const [letterGroupRefs] = useState(() => letterList.map(() => React.createRef<HTMLDivElement>()));

  const { attributeValue: currentLetter, scrollToElementByIndex } = useScrollingElementList(
    letterGroupRefs,
    alphabetPanelRef as RefObject<HTMLDivElement>,
    'data-letter'
  );

  useEffect(() => {
    history.replaceState({ letter: currentLetter }, document.title, `#${currentLetter}`);
  }, [alphabetLetterList, currentLetter]);

  const handleLetterClick = (event: React.MouseEvent<HTMLAnchorElement>, letter: string): void => {
    event.preventDefault();
    window.history.pushState({ letter }, document.title, event.currentTarget.href);
    const index = letterList.findIndex(x => x === letter);
    scrollToElementByIndex(index);
  };

  const renderLinkGroupList = (): React.JSX.Element => (
    <div className={'container'}>
      <div className={'py-8'}>
        {letterGroupList.map(({ letter, ordforklaringList }, index) => (
          <div className="row" key={letter}>
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="border-bottom-1 border-color-neutral500 pb-6 mb-6">
                <nav aria-label={`${linksForLetter} ${letter}`} ref={letterGroupRefs[index]} data-letter={letter}>
                  <Title htmlMarkup="h2" appearance="title2" id={letter.toUpperCase()} className="text-blueberry600">
                    {letter}
                  </Title>
                  {ordforklaringList && (
                    <ul className={classNames('alphabet-link-list mb-0')}>
                      {ordforklaringList.map((link, index) => {
                        return (
                          <li key={index} className="alphabet-link-list__item">
                            <Ordforklaring link={link} />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </nav>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {!hideAlphabetPanel && (
        <AlphabetPanel
          alphabetPanelTitle={alphabetPanelTitle}
          alphabetPanelButtonText={alphabetPanelButtonText}
          handleLetterClick={handleLetterClick}
          currentLetter={currentLetter}
          letterList={letterList}
          alphabetLetterList={alphabetLetterList}
          ref={alphabetPanelRef}
          emptyLetterAriaLabel={emptyLetterAriaLabel}
        />
      )}
      {letterGroupList && renderLinkGroupList()}
    </div>
  );
};

export default OppslagsverkPage;
